import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, ArrowRight, User } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');
      navigate(location.state?.from || '/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bk-bg)' }}>
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <Logo variant="light" size={56} className="mb-6 rounded-md overflow-hidden" />
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
              Marketplace Growth OS
            </p>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-3xl font-bold text-slate-900 tracking-tight">Entrar</CardTitle>
              <CardDescription className="text-slate-500">Acesse seu painel para continuar</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 font-semibold transition-opacity"
                  style={{ background: 'var(--bk-graphite)', color: 'var(--bk-cream)' }}
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Não tem uma conta?{' '}
                <Link to="/register" className="text-slate-900 hover:underline font-medium">
                  Criar conta
                </Link>
              </p>

              <p className="text-center text-[11px] text-slate-400 mt-4">
                <Link to="/privacy-policy" className="underline hover:text-slate-700" data-testid="login-privacy-link">Política de Privacidade</Link>
                {' · '}
                <Link to="/data-deletion" className="underline hover:text-slate-700" data-testid="login-deletion-link">Exclusão de Dados</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Brand panel */}
      <div
        className="hidden lg:flex lg:flex-1 relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'var(--bk-black)' }}
      >
        {/* Subtle texture */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 80%, rgba(232,226,216,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.04) 0%, transparent 50%)',
          }}
        />
        <div className="relative">
          <Logo variant="light" size={64} className="rounded-md overflow-hidden" />
        </div>
        <div className="relative">
          <h2
            className="text-4xl font-bold mb-4 leading-[1.1] tracking-tight"
            style={{ color: 'var(--bk-cream)' }}
          >
            Operação,<br />inteligência<br />e automação<br />em um só sistema.
          </h2>
          <p className="text-base text-white/55 max-w-md leading-relaxed">
            Plataforma de gestão para vendedores de marketplace. Diagnóstico,
            criação de anúncios em escala e execução assistida — tudo num só painel.
          </p>
          <div className="flex items-center gap-6 mt-8 text-xs text-white/40">
            <span>Mercado Livre</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Shopee</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Amazon</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>WhatsApp</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bk-bg)' }}>
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <Logo variant="light" size={56} className="mb-6 rounded-md overflow-hidden" />
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
              Marketplace Growth OS
            </p>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl font-bold text-slate-900">Criar Conta</CardTitle>
              <CardDescription>Comece a escalar suas vendas hoje</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Seu nome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 font-semibold transition-opacity"
                  style={{ background: 'var(--bk-graphite)', color: 'var(--bk-cream)' }}
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? 'Criando...' : 'Criar Conta'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Já tem uma conta?{' '}
                <Link to="/login" className="text-slate-900 hover:underline font-medium">
                  Entrar
                </Link>
              </p>

              <p className="text-center text-[11px] text-slate-400 mt-4">
                Ao se registrar, você concorda com nossa{' '}
                <Link to="/privacy-policy" className="underline hover:text-slate-700" data-testid="register-privacy-link">Política de Privacidade</Link>
                {' · '}
                <Link to="/data-deletion" className="underline hover:text-slate-700" data-testid="register-deletion-link">Exclusão de Dados</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Brand panel */}
      <div
        className="hidden lg:flex lg:flex-1 relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'var(--bk-black)' }}
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 80%, rgba(232,226,216,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.04) 0%, transparent 50%)',
          }}
        />
        <div className="relative">
          <Logo variant="light" size={64} className="rounded-md overflow-hidden" />
        </div>
        <div className="relative">
          <h2
            className="text-4xl font-bold mb-4 leading-[1.1] tracking-tight"
            style={{ color: 'var(--bk-cream)' }}
          >
            Comece a operar<br />como uma máquina.
          </h2>
          <p className="text-base text-white/55 max-w-md leading-relaxed">
            Crie sua conta e tenha acesso ao Bunker — gestão centralizada
            dos seus marketplaces, com automações que liberam o seu tempo.
          </p>
        </div>
      </div>
    </div>
  );
};
